import executeGetAllTasksRequest from "api/common/tasks.api";
import { TaskStatus } from "enums/task.enum";
import toTaskRowViewModel from "utils/task-mapper";
import { IGetAllTasksQuery, IGetAllTasksResponse, ITaskRowViewModel } from "types/task";

/** The three most recent tasks per status is what the merged history is built from - see ticket 02. */
export const RECENT_TASKS_PER_STATUS_PAGE_SIZE = 3;

export interface ITasksQuery {
    pageSize: number;
    scheduleId?: string;
    startKey?: string;
    total?: number;
}

export interface ITasksPage {
    rows: ITaskRowViewModel[];
    /** Total task count when the backend reports one - null when it does not, never fabricated as 0. */
    total: number | null;
    nextStartKey: string | null;
}

export interface ITasksRepository {
    getTasks(query: ITasksQuery): Promise<ITasksPage>;
    getRecentTasksByStatus(scheduleId: string, signal?: AbortSignal): Promise<ITaskRowViewModel[]>;
}

const EMPTY_PAGE: ITasksPage = { rows: [], total: null, nextStartKey: null };

const byScheduledDateTimeDesc = (a: ITaskRowViewModel, b: ITaskRowViewModel): number => {
    const aTime = a.scheduledDateTime?.getTime() ?? 0;
    const bTime = b.scheduledDateTime?.getTime() ?? 0;
    return bTime - aTime;
};

export const TasksRepository: ITasksRepository = {
    async getTasks(query: ITasksQuery): Promise<ITasksPage> {
        const response = await executeGetAllTasksRequest({
            pageSize: query.pageSize,
            scheduleId: query.scheduleId,
            startKey: query.startKey,
            total: query.total,
        } as IGetAllTasksQuery);

        if (!response || !Array.isArray(response.data)) {
            return EMPTY_PAGE;
        }

        return {
            rows: response.data.map(toTaskRowViewModel),
            total: response.total ?? null,
            nextStartKey: response.startKey,
        };
    },

    /**
     * Issues one request per task status for this schedule - all six, not just success/failed,
     * since ticket 01 exists specifically to surface every in-flight state (queued, in_progress,
     * suspended) rather than hide it, and the task history modal this feeds needs those visible
     * too. The backend has no after-datetime range filter, so "3 most recent" is enforced by the
     * backend's own pageSize + recency sort per status, not a client-side date bound. Results are
     * merged into up to 18 rows newest-first. Each status request is isolated via `allSettled` -
     * one status failing (or the schedule's requests failing outright) degrades this schedule's
     * list only, never throws to the caller.
     */
    async getRecentTasksByStatus(scheduleId: string, signal?: AbortSignal): Promise<ITaskRowViewModel[]> {
        const results = await Promise.allSettled(
            Object.values(TaskStatus).map((taskStatus) =>
                executeGetAllTasksRequest(
                    { pageSize: RECENT_TASKS_PER_STATUS_PAGE_SIZE, scheduleId, taskStatus },
                    { signal },
                ),
            ),
        );

        const rows = results
            .filter(
                (result): result is PromiseFulfilledResult<IGetAllTasksResponse | undefined> =>
                    result.status === "fulfilled",
            )
            .flatMap((result) => result.value?.data ?? [])
            .map(toTaskRowViewModel);

        return rows.sort(byScheduledDateTimeDesc);
    },
};

export default TasksRepository;
